import React, { useEffect, useMemo } from "react";
import Lottie from "lottie-react";
import { useSelector } from "react-redux";
import switchAnimation from "../../assets/switch.json";
import { translation } from "../../translation";

const getInitials = (profile, lang) => {
  if (!profile) return "?";
  const first =
    typeof profile.first_name === "string"
      ? profile.first_name
      : profile.first_name?.[lang] ||
        profile.first_name?.en ||
        profile.first_name?.fr ||
        "";
  const last =
    typeof profile.last_name === "string"
      ? profile.last_name
      : profile.last_name?.[lang] ||
        profile.last_name?.en ||
        profile.last_name?.fr ||
        "";
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";
};

const ProfileAvatarBubble = ({ profile, lang, className = "" }) => {
  const imageBase = process.env.REACT_APP_IMAGE_PATH;

  return (
    <div
      className={`flex h-14 w-14 items-center justify-center overflow-hidden rounded-full border-[3px] border-white bg-gradient-to-br from-gray-100 to-gray-200 shadow-lg ring-2 ring-black/5 ${className}`}
    >
      {profile?.profile_picture ? (
        <img
          src={`${imageBase}${profile.profile_picture}`}
          alt=""
          className="h-full w-full object-cover"
        />
      ) : (
        <span
          className={`text-[13px] font-bold text-gray-700 ${
            lang === "ar" ? "!font-readexBold" : "font-SteradianMedium"
          }`}
        >
          {getInitials(profile, lang)}
        </span>
      )}
    </div>
  );
};

const resolveProfiles = ({
  profiles,
  fromProfile,
  toProfile,
  toProfiles,
}) => {
  if (Array.isArray(profiles) && profiles.length) {
    return profiles.filter(Boolean).slice(0, 2);
  }
  if (Array.isArray(toProfiles) && toProfiles.length) {
    return toProfiles.filter(Boolean).slice(0, 2);
  }
  if (fromProfile && toProfile) {
    return [fromProfile, toProfile];
  }
  if (fromProfile) return [fromProfile];
  if (toProfile) return [toProfile];
  return [];
};

/**
 * Global centered loading overlay for profile switches.
 */
const ProfileSwitchOverlay = ({
  isOpen,
  message,
  profiles,
  fromProfile,
  toProfile,
  toProfiles,
}) => {
  const lang = useSelector(
    (state) => state.collaborator.collaboratorInfo.language,
  );

  const displayProfiles = useMemo(
    () =>
      resolveProfiles({ profiles, fromProfile, toProfile, toProfiles }),
    [profiles, fromProfile, toProfile, toProfiles],
  );

  const title =
    message || translation.settings.switchProfile.switchProfile[lang];

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const hasPair = displayProfiles.length >= 2;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 profile-switch-overlay-enter"
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={title}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[6px]" />

      {/* Ambient glow */}
      <div
        className="pointer-events-none absolute h-64 w-64 rounded-full bg-white/10 blur-3xl profile-switch-glow"
        aria-hidden="true"
      />

      <div className="relative w-full max-w-[300px] animate-fadeInScale">
        <div className="overflow-hidden rounded-[28px] border border-white/30 bg-white/90 shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl">
          <div className="h-1 w-full bg-gradient-to-r from-transparent via-gray-900 to-transparent profile-switch-shimmer-bar" />

          <div className="px-7 pb-7 pt-8">
            {/* Orbiting avatars */}
            <div className="relative mx-auto mb-7 h-[120px] w-[120px]">
              <div
                className="absolute inset-2 rounded-full border border-dashed border-gray-300/90 profile-switch-orbit-ring"
                aria-hidden="true"
              />
              <div
                className="absolute inset-5 rounded-full border border-gray-200/80"
                aria-hidden="true"
              />

              {hasPair ? (
                <>
                  <div className="profile-switch-orbit-a absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
                    <ProfileAvatarBubble
                      profile={displayProfiles[0]}
                      lang={lang}
                    />
                  </div>
                  <div className="profile-switch-orbit-b absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
                    <ProfileAvatarBubble
                      profile={displayProfiles[1]}
                      lang={lang}
                    />
                  </div>
                </>
              ) : displayProfiles.length === 1 ? (
                <div className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2 profile-switch-single-pulse">
                  <ProfileAvatarBubble
                    profile={displayProfiles[0]}
                    lang={lang}
                    className="h-16 w-16"
                  />
                </div>
              ) : null}

              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gray-900 shadow-lg ring-4 ring-white/80">
                  <Lottie
                    animationData={switchAnimation}
                    loop
                    className="h-7 w-7"
                  />
                </div>
              </div>
            </div>

            <p
              className={`text-center text-[17px] font-semibold leading-snug text-gray-900 ${
                lang === "ar" ? "!font-readexMedium" : "font-SteradianMedium"
              }`}
            >
              {title}
            </p>

            <div className="mt-5 flex items-center justify-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-gray-800 animate-dotBounce [animation-delay:0ms]" />
              <span className="h-1.5 w-1.5 rounded-full bg-gray-500 animate-dotBounce [animation-delay:150ms]" />
              <span className="h-1.5 w-1.5 rounded-full bg-gray-300 animate-dotBounce [animation-delay:300ms]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSwitchOverlay;
